/**
 * Ingest's job list rule, for the one picker (Rigs and Handheld:
 * src/apps/ingest/views/IngestJobPicker.jsx). Pure: no Firebase here. The
 * jobs come from the listIngestJobs callable (functions/src/services/
 * ingestList.ts) as slim records: { id, jobId, name, what, status, invoiced,
 * paid, trashed, days, guest, walkins, walkinsClosed, registrationOpen,
 * rigCaptures, handheld, review, reviewAt, processing, failed, failedAt,
 * guestPages, unfinished, checked, lastActivity, list }.
 *
 * The approved design: mockups/ingest/job-list-v2.html (claude/ingest-job-
 * list-mockup), with Ivin's picks (2026-10-09/10): a 14-day quiet period, and
 * guest pages keep a job listed for the first 30 days after their email.
 *
 * A job is in Ingest automatically when any of these is true:
 *   1. it has a shoot day in the next 7 days or the last 3 days;
 *   2. it is booked, on set or processing, and something happened on it in
 *      the last 14 days (an edit, a capture, an upload, processing, a
 *      handheld subject);
 *   3. it still has work open: captures waiting for review, processing not
 *      finished or failed, guest pages emailed in the last 30 days, a capture
 *      not finished, registration open.
 * Delivered, invoiced and paid jobs come in by 1 and 3 only; so does a past
 * month's studio walk-ins job. A job with any other status (or none) never
 * does. Trashed jobs never show (Find lists them, and they do not open).
 *
 * By hand (shared by everyone, stored on the server): Pinned keeps a job in
 * until someone unpins it; Archived takes it out of this list only. An
 * archived job comes back on its own when something needs attention after it
 * was archived: a capture waiting for review, or a processing failure
 * ("Back: …"). The server records the return (list mode "back"), so the job
 * then follows the rule like any other until someone archives it again. A
 * pinned job cannot be archived (unpin it first).
 */

export const QUIET_DAYS = 14;
export const GUEST_PAGE_DAYS = 30;
export const NEAR_AHEAD_DAYS = 7;
export const NEAR_BEHIND_DAYS = 3;
export const LIVE_STATUSES = ["scheduled", "in_progress", "processing"];
export const FIND_MAX = 50;

const DAY = 86400000;
const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MO = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/* ── dates: YYYY-MM-DD, New York time ── */

const NY_FMT = typeof Intl !== "undefined"
  ? new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit" })
  : null;
/** The New York calendar day of a moment, YYYY-MM-DD. */
export function nyDay(ms) {
  const parts = Object.fromEntries(NY_FMT.formatToParts(new Date(ms)).map((p) => [p.type, p.value]));
  return `${parts.year}-${parts.month}-${parts.day}`;
}
const dms = (d) => Date.UTC(+d.slice(0, 4), +d.slice(5, 7) - 1, +d.slice(8, 10));
/** a minus b, in days. */
export const diff = (a, b) => Math.round((dms(a) - dms(b)) / DAY);
const wd = (d) => WD[new Date(dms(d)).getUTCDay()];
const dayNo = (d) => +d.slice(8, 10);
/** "Oct 9" (no line break inside a date). */
export const short = (d) => `${MO[+d.slice(5, 7) - 1]}\u00a0${dayNo(d)}`;
export const longDay = (d) => `${wd(d)} ${short(d)}`;
const range = (a, b) => (a.slice(0, 7) === b.slice(0, 7) ? `${short(a)}–${dayNo(b)}` : `${short(a)}–${short(b)}`);
const relDay = (d, today) => {
  const n = diff(d, today);
  if (n === 0) return "Today";
  if (n === 1) return "Tomorrow";
  if (n === -1) return "Yesterday";
  if (n > 1 && n < 7) return wd(d);
  return short(d);
};
export const weekdayOf = wd;
export const dayNumberOf = dayNo;

/* ── words ── */

export const STATUS_LABEL = { scheduled: "Booked", in_progress: "On set", processing: "Processing", delivered: "Delivered" };
export const statusLabel = (s) => STATUS_LABEL[s] ?? (s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, " ") : "No status");
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** The shoot day that puts a job in "Today" / "This week": today, else the next in the coming 7 days, else the latest of the last 3. */
export function keyDay(job, today) {
  const days = Array.isArray(job?.days) ? job.days : [];
  if (days.includes(today)) return today;
  const near = days.filter((d) => diff(d, today) >= -NEAR_BEHIND_DAYS && diff(d, today) <= NEAR_AHEAD_DAYS);
  return near.find((x) => diff(x, today) > 0) ?? near[near.length - 1] ?? null;
}

/** Days since the last activity (New York days), or null when there was none. */
export function quietDays(job, today) {
  if (typeof job?.lastActivity !== "number") return null;
  return Math.max(0, diff(today, nyDay(job.lastActivity)));
}

/** What is open on the job now, as words ("2 captures waiting for review"). */
export function openWork(job) {
  const open = [];
  if (job.review) open.push(`${plural(job.review, "capture")} waiting for review`);
  if (job.failed) open.push(`${plural(job.failed, "processing failure")}`);
  if (job.processing) open.push(`${job.processing} still processing`);
  if (job.held) open.push(`${job.held} held for processing`);
  if (job.guestPages) open.push(`${plural(job.guestPages, "guest page")} live (emailed in the last ${GUEST_PAGE_DAYS} days)`);
  if (job.unfinished) open.push(`${plural(job.unfinished, "capture")} not finished`);
  if (job.registrationOpen) open.push("registration open");
  return open;
}

// A sentence: an array of parts, each a string or { b: string } (bold).
const S = (...parts) => parts;

/**
 * The rule alone: { on, why: [sentence], kind, open } where kind names the
 * group: "today" | "week" | "working" | "after" | "off".
 */
export function autoRule(job, { today }) {
  if (job.trashed) return { on: false, why: [S("It is in the trash. Trashed jobs never show.")], kind: "off", open: 0 };
  const closedWalkins = Boolean(job.walkinsClosed);
  const live = LIVE_STATUSES.includes(job.status) && !closedWalkins;
  const delivered = job.status === "delivered" || closedWalkins;
  const unchecked = job.checked === false ? " Its captures could not be checked just now." : "";
  if (!live && !delivered) {
    return {
      on: false, kind: "off", open: 0,
      why: [S(job.status ? `It is marked ${statusLabel(job.status).toLowerCase()}` : "It has no status", ": only booked, on-set, processing and delivered jobs come into Ingest.")],
    };
  }
  const why = [];
  let kind = null;
  const d = keyDay(job, today);
  if (d === today) { why.push(S("It has a shoot day ", { b: "today" }, ".")); kind = "today"; } else if (d) {
    const n = diff(d, today);
    why.push(n > 0
      ? S("It has a shoot day ", { b: n === 1 ? "tomorrow" : `on ${longDay(d)}` }, ".")
      : S("Its shoot day was ", { b: n === -1 ? "yesterday" : longDay(d) }, ` (within the last ${NEAR_BEHIND_DAYS} days).`));
    kind = "week";
  }
  if (job.status === "in_progress" && !closedWalkins) why.push(S("It is marked ", { b: "on set" }, " (in progress)."));
  const open = openWork(job);
  if (open.length) {
    why.push(S("It still has work open: ", { b: open.join(", ") }, "."));
    kind = kind ?? (delivered ? "after" : "working");
  }
  // Its captures, processing or handheld work could not be read just now:
  // it stays listed rather than drop out on a failed read.
  if (!kind && job.checked === false) {
    why.push(S("Its captures could not be checked just now, so it ", { b: "stays listed" }, " until they can be."));
    kind = delivered ? "after" : "working";
  }
  const quiet = quietDays(job, today);
  if (!kind && !delivered) {
    const label = statusLabel(job.status).toLowerCase();
    if (quiet !== null && quiet <= QUIET_DAYS) {
      const when = quiet === 0 ? "today" : quiet === 1 ? "yesterday" : `${quiet} days ago`;
      why.push(S("It is ", { b: label }, ` and was last touched ${when} (it drops out after more than ${QUIET_DAYS} quiet days).`));
      kind = "working";
    } else {
      return {
        on: false, kind: "off", open: 0,
        why: [quiet === null
          ? S(`It is still marked ${label}, but nothing has happened on it and it has no shoot day in the next ${NEAR_AHEAD_DAYS} days.${unchecked}`)
          : S(`It is still marked ${label}, but nothing has happened for `, { b: `${quiet} days` }, ` (more than ${QUIET_DAYS}) and it has no shoot day in the next ${NEAR_AHEAD_DAYS} days.${unchecked}`)],
      };
    }
  }
  if (kind && job.status === "in_progress" && kind !== "today" && kind !== "week") kind = "working";
  if (!kind) {
    return {
      on: false, kind: "off", open: 0,
      why: [S(closedWalkins
        ? `Its month of studio walk-ins is over and nothing is left open.${unchecked}`
        : `It is delivered and nothing is left open (no review, no processing, no guest pages emailed in the last ${GUEST_PAGE_DAYS} days).${unchecked}`)],
    };
  }
  return { on: true, why, kind, open: open.length + (job.checked === false ? 1 : 0) };
}

/**
 * Why an archived job came back on its own: what needed attention after it
 * was archived, { at, note }, or null. Captures waiting for review, or a
 * processing failure; plain activity never.
 */
export function backOf(job) {
  const s = job?.list;
  // Came back, and the server already recorded it (listIngestJobs): it stays
  // back (automatic) until someone archives it again.
  if (s?.mode === "back") return { at: typeof s.backAt === "number" ? s.backAt : s.at, note: s.note || "something needed attention" };
  if (!s || s.mode !== "archive" || typeof s.at !== "number") return null;
  const notes = [];
  let at = null;
  if (job.review && typeof job.reviewAt === "number" && job.reviewAt > s.at) {
    notes.push(`${plural(job.review, "capture")} to review`);
    at = Math.max(at ?? 0, job.reviewAt);
  }
  if (job.failed && typeof job.failedAt === "number" && job.failedAt > s.at) {
    notes.push(`${plural(job.failed, "processing failure")}`);
    at = Math.max(at ?? 0, job.failedAt);
  }
  return notes.length ? { at, note: notes.join(", ") } : null;
}

/**
 * The job's place: { on, a (autoRule), src: "trash" | "pin" | "archive" |
 * "auto", back (an archived job that came back, else null) }.
 */
export function stateOf(job, ctx) {
  const a = autoRule(job, ctx);
  if (job.trashed) return { on: false, a, src: "trash", back: null };
  const mode = job.list?.mode;
  if (mode === "pin") return { on: true, a, src: "pin", back: null };
  if (mode === "back") return { on: a.on, a, src: "auto", back: backOf(job) };
  if (mode === "archive") {
    const back = backOf(job);
    if (back && a.on) return { on: true, a, src: "auto", back };
    return { on: false, a, src: "archive", back: null };
  }
  return { on: a.on, a, src: "auto", back: null };
}

/** "you" or the name of whoever pinned or archived it. */
export const whoOf = (list) => (list?.byMe ? "you" : (list?.byName || "someone"));
/** "Ethan · Oct 3" */
export const byText = (list) => (list ? `${whoOf(list)} · ${short(nyDay(list.at))}` : "");

/** One short line: why the job is in the list (or archived). */
export function shortWhy(job, st, today) {
  if (st.src === "pin") return "Pinned by hand";
  if (st.src === "archive") return `Archived by ${byText(job.list)}`;
  if (st.src === "trash") return "In the trash";
  if (st.back) return `Back: ${st.back.note}`;
  const k = st.a.kind;
  if (k === "today") return "Shoot day today";
  if (k === "week") return (job.days || []).some((d) => diff(d, today) > 0) ? "Shoot day this week" : `Shot in the last ${NEAR_BEHIND_DAYS} days`;
  if (k === "after") return "Work still open after delivery";
  if (st.a.open) return "Work still open";
  if (k === "working") return `${statusLabel(job.status)}, touched in the last ${QUIET_DAYS} days`;
  return "Not in Ingest";
}

/** Human dates: "Today", "Tomorrow", "Fri Oct 17", "Wed Oct 14–15", "Today · Oct 9–10", "Shot Oct 1–6". */
export function whenOf(job, today) {
  const days = Array.isArray(job?.days) ? job.days : [];
  if (!days.length) return { text: "No shoot day", past: true };
  const f = days[0];
  const l = days[days.length - 1];
  const nf = diff(f, today);
  const nl = diff(l, today);
  if (days.length === 1) {
    if (nf === 0 || nf === 1 || nf === -1) return { text: relDay(f, today), past: nf < 0 };
    if (nf > 1 && nf <= 60) return { text: longDay(f), past: false };
    if (nf > 60) return { text: short(f), past: false };
    return { text: nf >= -3 ? `Shot ${longDay(f)}` : `Shot ${short(f)}`, past: true };
  }
  if (days.includes(today)) return { text: `Today · ${range(f, l)}`, past: false };
  if (nl < 0) return { text: `Shot ${range(f, l)}`, past: true };
  const u = days.find((d) => diff(d, today) > 0);
  const nu = diff(u, today);
  if (nu === 1) return { text: `Tomorrow · ${range(f, l)}`, past: false };
  if (u !== f) return { text: `${longDay(u)} · ${range(f, l)}`, past: false };
  if (nf <= 60) return { text: `${wd(f)} ${range(f, l)}`, past: false };
  return { text: range(f, l), past: false };
}

/* ── the screen's job set ── */

/** The jobs a screen works with: Handheld leaves out guest jobs and the studio walk-ins jobs (rig only). */
export function jobsForScreen(jobs, screen) {
  const list = Array.isArray(jobs) ? jobs : [];
  if (screen !== "handheld") return list;
  return list.filter((j) => !j.guest && !j.walkins);
}

const firstDay = (j, today) => (j.days || []).find((d) => diff(d, today) >= -NEAR_BEHIND_DAYS) ?? (j.days || [])[(j.days || []).length - 1] ?? "9999-12-31";
const byName = (a, b) => (a.name || "").localeCompare(b.name || "");
const byDayThenName = (today) => (a, b) => firstDay(a, today).localeCompare(firstDay(b, today)) || byName(a, b);

/** Layout A's groups: [{ key, title, jobs, hint }], empty groups left out. */
export function groupsA(jobs, ctx, states) {
  const { today } = ctx;
  const g = { pin: [], today: [], week: [], working: [], after: [] };
  for (const j of jobs) {
    const st = states.get(j.id);
    if (!st?.on) continue;
    if (st.src === "pin") g.pin.push(j);
    else if (g[st.a.kind]) g[st.a.kind].push(j);
  }
  return [
    { key: "pin", title: "Pinned", jobs: g.pin, hint: "kept in by hand" },
    { key: "today", title: "Today", jobs: g.today, hint: longDay(today) },
    { key: "week", title: "This week", jobs: g.week, hint: `shoot days in the next ${NEAR_AHEAD_DAYS} days or the last ${NEAR_BEHIND_DAYS}` },
    { key: "working", title: "Still working", jobs: g.working, hint: "booked, on set or processing: recent activity or work still open" },
    { key: "after", title: "Active after delivery", jobs: g.after, hint: "guest pages, review or processing still open" },
  ].map((x) => ({ ...x, jobs: x.jobs.sort(byDayThenName(today)) })).filter((x) => x.jobs.length);
}

/** Layout B's groups: pinned, then one per shoot day (today and coming days first, then days just past), then the rest. */
export function groupsB(jobs, ctx, states) {
  const { today } = ctx;
  const pin = [];
  const byDay = new Map();
  const working = [];
  const after = [];
  for (const j of jobs) {
    const st = states.get(j.id);
    if (!st?.on) continue;
    if (st.src === "pin") { pin.push(j); continue; }
    if (st.a.kind === "today" || st.a.kind === "week") {
      const d = keyDay(j, today);
      if (!byDay.has(d)) byDay.set(d, []);
      byDay.get(d).push(j);
    } else if (st.a.kind === "after") after.push(j);
    else working.push(j);
  }
  const days = [...byDay.keys()].sort((a, b) => {
    const da = diff(a, today);
    const db = diff(b, today);
    if ((da >= 0) !== (db >= 0)) return da >= 0 ? -1 : 1;
    return da >= 0 ? da - db : db - da;
  });
  const out = [];
  if (pin.length) out.push({ key: "pin", title: "Pinned", jobs: pin.sort(byDayThenName(today)), hint: "kept in by hand", kind: "hand" });
  for (const d of days) {
    const n = diff(d, today);
    const l = byDay.get(d).sort(byName);
    const past = l.every((j) => j.status !== "delivered") ? "shot, not delivered yet" : `shot in the last ${NEAR_BEHIND_DAYS} days`;
    out.push({
      key: `day-${d}`,
      title: n === 0 ? "Today" : n === 1 ? "Tomorrow" : n === -1 ? "Yesterday" : longDay(d),
      jobs: l,
      hint: n === 0 || n === 1 ? longDay(d) : n < 0 ? past : `in ${n} days`,
      day: d,
      kind: n === 0 ? "today" : n < 0 ? "past" : "",
    });
  }
  if (working.length) out.push({ key: "working", title: "Still working", jobs: working.sort(byDayThenName(today)), hint: "no shoot day this week: recent activity or work still open" });
  if (after.length) out.push({ key: "after", title: "Active after delivery", jobs: after.sort(byDayThenName(today)), hint: "guest pages, review or processing still open" });
  return out;
}

/**
 * Tidy-up candidates: in the list by the rule alone, no shoot day near, and
 * nothing has happened on them for more than the quiet period (most quiet first).
 */
export function tidyList(jobs, ctx, states) {
  return jobs
    .filter((j) => {
      const st = states.get(j.id);
      if (!st?.on || st.src !== "auto") return false;
      // Never a job whose captures could not be checked: it may have work open.
      if (j.checked === false) return false;
      // A job that came back lately is not offered again so soon.
      if (st.back && diff(ctx.today, nyDay(st.back.at)) <= QUIET_DAYS) return false;
      if (st.a.kind === "today" || st.a.kind === "week") return false;
      const q = quietDays(j, ctx.today);
      return q === null || q > QUIET_DAYS;
    })
    .sort((a, b) => (quietDays(b, ctx.today) ?? Infinity) - (quietDays(a, ctx.today) ?? Infinity));
}

export const quietText = (job, today) => {
  const n = quietDays(job, today);
  if (n === null) return "no activity";
  return n === 0 ? "touched today" : n === 1 ? "touched yesterday" : `quiet for ${n} days`;
};

const norm = (s) => (typeof s === "string" ? s : "").toLowerCase();

/**
 * Find: every job (in Ingest or not, archived and trashed included) whose
 * name, what, job id or document id contains every word typed. Jobs in
 * Ingest first, then the rest; newest activity first in each. At most `max`.
 */
export function findJobs(jobs, term, states, { max = FIND_MAX } = {}) {
  const words = norm(term).trim().split(/\s+/).filter(Boolean);
  if (!words.length) return { hits: [], total: 0 };
  const all = (Array.isArray(jobs) ? jobs : []).filter((j) => {
    const hay = [j.name, j.what, j.jobId, j.id].map(norm).join(" ");
    return words.every((w) => hay.includes(w));
  });
  all.sort((a, b) => (Number(Boolean(states.get(b.id)?.on)) - Number(Boolean(states.get(a.id)?.on)))
    || ((b.lastActivity ?? 0) - (a.lastActivity ?? 0)) || byName(a, b));
  return { hits: all.slice(0, max), total: all.length };
}

/** Put back: automatic when the rule alone shows it, else pinned so it stays. */
export const putBackMode = (st) => (st.a.on ? "auto" : "pin");

/** Every job's state, by id. */
export function statesOf(jobs, ctx) {
  return new Map((jobs || []).map((j) => [j.id, stateOf(j, ctx)]));
}
